import Criterion from '../../interface/v1/Criterion';
import {
  ICriterionService,
  CriterionServiceDependenciesType
} from './ICriterionService';
import bindAllMethods from '../../utils/bindAllMethods';
import { AuthContextEnum } from '../authTokenService';
import { AccessControlOptionsType } from '../../interface/v1/accessControl/types';
import {
  OfferingAndEntitlementType,
  OfferingAndEntitlementOptionsType
} from '../../interface/v1/entitlements/types';
import { GrantOptionsType, GrantType } from '../grants/types';
import {
  GrantHistoryOptionsType,
  GrantHistoryType
} from '../grants/grantsHistory/types';
import valueFunctions from '../../interface/v1/Criterion/valueFunctions';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import { ServiceLocalizationLanguageType } from '../localizationService/types';
import { isNative as isNativeFromJWeb } from '../JWeb';
import { MathOpType } from '../../interface/v1/Criterion/types';
import { EligibilityRuleInput } from '../eligibilityService/types';
import { SetServiceDependencies } from '../../infra/startup/types';
import { IEventService } from '../eventService';
import { IEntitlementsService } from '../entitlementsService';
import { IGrantService } from '../grants/IGrantService';
import { IGrantHistoryService } from '../grants/grantsHistory/IGrantHistoryService';
import { ISessionService } from '../session';
import { IAccessControlService } from '../accessControlService';
import { IEligibilityService } from '../eligibilityService/IEligibilityService';
import ITenantHandlerService from '../tenantHandler/ITenantHandlerService';
import { IFeatureFlagService } from '../featureFlagService';
import { IUserService } from '../userService';
import { ILocalizationService } from '../localizationService';

export class CriterionService implements ICriterionService {
  private _criterionInstance: Criterion<any>;
  private _eventService: IEventService;
  private _entitlementsService: IEntitlementsService;
  private _grantService: IGrantService;
  private _grantHistoryService: IGrantHistoryService;
  private _sessionService: ISessionService;
  private _accessControlService: IAccessControlService;
  private _eligibilityService: IEligibilityService;
  private _tenantHandlerService: ITenantHandlerService;
  private _featureFlagService: IFeatureFlagService;
  private _userService: IUserService;
  private _localizationService: ILocalizationService;
  private _criterions: CriterionServiceDependenciesType['criterions'];

  constructor(options?: CriterionServiceDependenciesType) {
    this._criterions = options?.criterions || [];
    bindAllMethods(this);
  }

  public setDependencies({ services }: SetServiceDependencies): void {
    this._eventService = services.eventService;
    this._entitlementsService = services.entitlementsService;
    this._grantService = services.grantService;
    this._grantHistoryService = services.grantHistoryService;
    this._sessionService = services.sessionService;
    this._accessControlService = services.accessControlService;
    this._eligibilityService = services.eligibilityService;
    this._tenantHandlerService = services.tenantHandlerService;
    this._featureFlagService = services.featureFlagService;
    this._userService = services.userService;
    this._localizationService = services.localizationService;
  }

  public async init(): Promise<void> {
    if (this._criterionInstance) {
      return;
    }

    this._criterionInstance = new Criterion({
      valueFunctions: {
        // @ts-ignore
        entitlement: async (
          value: OfferingAndEntitlementType,
          criterionOptions?: OfferingAndEntitlementOptionsType
        ) =>
          !!(await this._entitlementsService.checkEntitlements(
            [value],
            criterionOptions
          )),
        // @ts-ignore
        offering: async (
          value: OfferingAndEntitlementType,
          criterionOptions?: OfferingAndEntitlementOptionsType
        ) =>
          !!(await this._entitlementsService.checkOfferings(
            [value],
            criterionOptions
          )),
        // @ts-ignore
        scope: async (
          value: string,
          criterionOptions?: AccessControlOptionsType
        ) => {
          let hasScopes = false;
          const loggedIn = !!this._sessionService.isLoggedIn();
          if (loggedIn) {
            hasScopes = !!(await this._accessControlService.checkScopes(
              [{ scope: value }],
              criterionOptions
            ));
          }
          return hasScopes;
        },
        // @ts-ignore
        action: async (
          value: string,
          criterionOptions?: AccessControlOptionsType
        ) => {
          let isAllowed = false;
          const loggedIn = !!this._sessionService.isLoggedIn();
          if (loggedIn) {
            isAllowed = !!(await this._accessControlService.check(
              [value],
              criterionOptions
            ));
          }
          return isAllowed;
        },
        isLoggedIn: async (value: boolean) => {
          const isLoggedIn = !!this._sessionService?.isLoggedIn?.();

          return value ? isLoggedIn : !isLoggedIn;
        },
        authContext: async (value: { context: AuthContextEnum }) => {
          const authContext = value?.context;

          if (!authContext) return false;

          const currentAuthContext =
            this._tenantHandlerService.getCurrentContext();

          return authContext === currentAuthContext;
        },
        featureFlag: async (value: {
          clientKey: string;
          featureFlag: string;
          value: unknown;
          defaultValue: unknown;
        }) => {
          const client = await this._featureFlagService.getClient(
            value?.clientKey
          );
          const featureFlag = await client.getFeatureFlag({
            key: value?.featureFlag,
            defaultValue: value?.defaultValue
          });
          return featureFlag === value?.value;
        },
        userDetails: async (value: { countries: string[] }) => {
          const country = await this._userService.getCountry();
          return value?.countries
            ?.map((c) => c.toLocaleLowerCase())
            ?.includes(country);
        },
        locale: async (value: {
          languages: ServiceLocalizationLanguageType;
        }) => {
          return !!(await this._localizationService.checkAllowedLanguages(
            value?.languages
          ));
        },
        // @ts-ignore
        grant: async (
          value: GrantType,
          criterionOptions?: GrantOptionsType
        ) => {
          return !!(await this._grantService.checkGrants(
            [value],
            criterionOptions
          ));
        },

        grantHistory: async (
          value: GrantHistoryType,
          criterionOptions?: GrantHistoryOptionsType
        ) => {
          return !!(await this._grantHistoryService.checkGrantsHistory(
            [value],
            criterionOptions
          ));
        },
        // @ts-ignore
        eligibility: async (
          value: EligibilityRuleInput & { public?: boolean | string }
        ) => {
          const results = await this._eligibilityService.checkEligibility(
            [{ rulesSetId: value?.rulesSetId, data: value?.data }],
            !!value?.public
          );

          return !!results?.find(
            (item) => item?.rulesSetId === value?.rulesSetId
          )?.isEligible;
        },
        // TODO: this should be fixed ASAP
        // @ts-ignore
        pendingGrant: async (
          value: GrantType,
          criterionOptions?: GrantOptionsType
        ) => {
          // @ts-ignore
          return !!(await this._grantService.checkPendingGrants(
            [value],
            criterionOptions
          ));
        },
        tenant: async (value: {
          type?: string[];
          roleCategory?: string;
          roleName?: string;
          countries?: string[];
          regionId?: string;
        }) => {
          try {
            const isNative = await isNativeFromJWeb();
            const tenant = this._tenantHandlerService.getTenantByContext(
              AuthContextEnum.tenant
            );
            // @TODO remove this workaround when checkCriterion occurs only after portals have a chosen tenant.
            if (!isNative && !tenant) {
              return true;
            }

            let checkRoleCategory = true;
            let checkRoleName = true;
            let checkTenantType = true;
            let checkTenantCountries = true;
            let checkTenantRegionId = true;

            if (value?.roleCategory) {
              const valueLower = value.roleCategory.toLowerCase();
              if (tenant?.data?.roles?.length) {
                checkRoleCategory = tenant.data.roles.some(
                  (r) => r.category?.toLowerCase() === valueLower
                );
              } else {
                checkRoleCategory =
                  valueLower === tenant?.data?.roleCategory?.toLowerCase();
              }
            }
            if (value?.roleName) {
              const valueLower = value.roleName.toLowerCase();
              if (tenant?.data?.roles?.length) {
                checkRoleName = tenant.data.roles.some(
                  (r) => r.name?.toLowerCase() === valueLower
                );
              } else {
                checkRoleName =
                  valueLower === tenant?.data?.roleName?.toLowerCase();
              }
            }

            if (value?.type) {
              checkTenantType = !!value?.type
                ?.map((t) => t.toLowerCase())
                ?.includes(tenant?.data?.type?.toLowerCase());
            }

            if (value?.countries) {
              const { countries } =
                await this._tenantHandlerService.getCurrentTenantAdditionalData();
              checkTenantCountries = countries.some((country) =>
                value.countries?.includes(country)
              );
            }

            if (value?.regionId) {
              const { regionId } =
                await this._tenantHandlerService.getCurrentTenantAdditionalData();
              checkTenantRegionId = value.regionId === regionId;
            }

            return (
              checkRoleCategory &&
              checkRoleName &&
              checkTenantType &&
              checkTenantCountries &&
              checkTenantRegionId
            );
          } catch (error) {
            internalLogger?.error(
              'The tenant criteria is poorly formatted ',
              error
            );
            return false;
          }
        },

        tenantList: async (value: {
          count?: { inputValue: string; mathOp: MathOpType };
        }) => {
          return this._evaluateTenantListCount(value?.count);
        },
        // @ts-ignore
        programInfo: async (
          value: { programLevel: string; state?: string },
          criterionOptions?: OfferingAndEntitlementOptionsType
        ) => {
          const programInfo = await this._entitlementsService.getPlanInfo(
            criterionOptions
          );

          if (value?.state && value?.programLevel) {
            return (
              programInfo?.programLevel == value.programLevel &&
              programInfo?.state == value.state
            );
          } else if (value?.state) {
            return programInfo?.state == value?.state;
          } else if (value?.programLevel) {
            return programInfo?.programLevel == value?.programLevel;
          }

          return false;
        },

        ...valueFunctions
      },
      criterionList: this._criterions as any,
      eventService: this._eventService,
      eventList: [
        this._eventService.eventNames.shellAccessControlChangedEventName,
        this._eventService.eventNames.shellEntitlementChangedEventName,
        this._eventService.eventNames.shellUserLoggedInEventName,
        this._eventService.eventNames.shellUserLoggedOutEventName,
        this._eventService.eventNames.shellFeatureFlagChangedEventName,
        this._eventService.eventNames.shellGrantsChangedEventName,
        this._eventService.eventNames.shellPendingGrantsChangedEventName,
        this._eventService.eventNames.shellGrantsHistoryChangedEventName,
        this._eventService.eventNames.shellTenantChangedEventName,
        this._eventService.eventNames.shellUrlChanged,
        this._eventService.eventNames.shellCriterionForceCheckUpdate
      ],
      eventName: this._eventService.eventNames.shellCriterionChangedEventName
    });

    this._criterionInstance.addValueFunctions({
      criterion: async (value: string) => {
        return this._criterionInstance.checkByCriterionKey(value);
      }
    });

    await this._criterionInstance.waitForListenersReady();
  }
  // Legacy support - returns the full Criterion class instance for backward compatibility
  public getCriterionInstance(): Criterion<any> {
    if (!this._criterionInstance) {
      throw new Error('CriterionService not initialized. Call init() first.');
    }
    return this._criterionInstance;
  }

  // TODO: Remove this when all code is migrated to use CriterionInterfaceType
  public getCriterionLegacy(): Criterion<any> {
    return this.getCriterionInstance();
  }

  private _tenantListCountOperations: Record<
    MathOpType,
    (current: number, expected: number) => boolean
  > = {
    eq: (c, e) => c === e,
    ne: (c, e) => c !== e,
    gt: (c, e) => c > e,
    gte: (c, e) => c >= e,
    lt: (c, e) => c < e,
    lte: (c, e) => c <= e
  };

  private _parseCountInput(raw: string): number | null {
    if (raw === undefined || raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }

  private async _evaluateTenantListCount(countConfig?: {
    inputValue: string;
    mathOp: MathOpType;
  }): Promise<boolean> {
    if (!countConfig) {
      internalLogger?.error('tenantList criterion: missing countConfig');
      return false;
    }

    const parsed = this._parseCountInput(countConfig.inputValue);

    if (parsed === null) {
      internalLogger?.error(
        `tenantList criterion: inputValue="${countConfig.inputValue}" is not a valid number`
      );
      return false;
    }

    try {
      const tenantList = await this._tenantHandlerService.getTenantList({
        authContext: AuthContextEnum.tenant
      });
      const size = Array.isArray(tenantList) ? tenantList.length : 0;
      return this._tenantListCountOperations[countConfig.mathOp](size, parsed);
    } catch (error) {
      internalLogger?.error('Error evaluating tenantList criterion:', error);
      return false;
    }
  }
}

export default CriterionService;
